import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, Lock, CheckCircle, ArrowRight, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { siteContent } from '../data';

const Services = () => {
  const iconMap = { Shield, FileText, Lock };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-cyan-900/20 via-black to-black"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-20 w-72 h-72 bg-green-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6 backdrop-blur-sm">
              <Shield className="w-4 h-4 text-cyan-400 mr-2" />
              <span className="text-cyan-400 text-sm font-medium">Professional Security Services</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">{siteContent.services.title}</h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">{siteContent.services.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="pb-20 relative">
        <div className="container mx-auto px-4">
          <div className="space-y-12 max-w-5xl mx-auto">
            {siteContent.services.items.map((service, idx) => {
              const Icon = iconMap[service.icon] || Shield;
              return (
                <Card key={idx} className="bg-gray-900/30 border-cyan-900/30 backdrop-blur-sm hover:bg-gray-900/50 transition-all duration-300 hover:border-cyan-500/50 hover:shadow-lg hover:shadow-cyan-500/10 overflow-hidden">
                  <div className="md:flex">
                    <div className="md:w-1/3 bg-gradient-to-br from-cyan-900/30 to-green-900/30 p-8 flex items-center justify-center">
                      <div className="w-24 h-24 bg-gradient-to-br from-cyan-500/30 to-green-500/30 rounded-2xl flex items-center justify-center">
                        <Icon className="w-12 h-12 text-cyan-400" />
                      </div>
                    </div>
                    <div className="md:w-2/3 p-8">
                      <CardHeader className="p-0 mb-4">
                        <CardTitle className="text-white text-3xl mb-2">{service.name}</CardTitle>
                        <p className="text-gray-300 text-lg leading-relaxed">{service.description}</p>
                      </CardHeader>
                      <CardContent className="p-0">
                        <h4 className="text-cyan-400 font-semibold mb-4">Key Features:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {service.features.map((feature, fidx) => (
                            <div key={fidx} className="flex items-start space-x-2">
                              <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                              <span className="text-gray-400 text-sm">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">How It Works</h2>
            <p className="text-gray-400 text-lg">Our streamlined protection process</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {[
              { step: '01', title: 'Assessment', desc: 'We analyze your digital footprint and identify vulnerabilities' },
              { step: '02', title: 'Monitoring', desc: 'Deploy 24/7 automated monitoring across all channels' },
              { step: '03', title: 'Detection', desc: 'AI-powered systems identify threats in real-time' },
              { step: '04', title: 'Response', desc: 'Immediate action to neutralize and remove threats' }
            ].map((item, idx) => (
              <div key={idx} className="text-center relative">
                <div className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-cyan-500/30 to-transparent mb-4">
                  {item.step}
                </div>
                <h3 className="text-white font-semibold text-xl mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
                {idx < 3 && (
                  <div className="hidden md:block absolute top-8 -right-4 text-cyan-500/30">
                    <ArrowRight className="w-8 h-8" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-cyan-900/30 to-green-900/30 backdrop-blur-sm rounded-3xl p-12 border border-cyan-900/50 max-w-4xl mx-auto text-center">
            <Zap className="w-12 h-12 text-cyan-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Need Custom Protection?</h2>
            <p className="text-gray-300 mb-8">Contact us to discuss your specific security requirements</p>
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-to-r from-cyan-500 to-green-500 hover:from-cyan-600 hover:to-green-600 text-black font-semibold px-8 py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/25">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;